import React, { useMemo } from 'react';

import { Alert } from '../../../../../shared/styles/components/feedback.styles';
import type { Item } from '../../../types/item.types';
import { tokens } from '../../../../../shared/styles/core/tokens';

const MAX_LISTED = 3;

interface CrossFamilyWarningProps {
    resolvedSelectedItems: Item[];
    familyName: string | undefined;
}

export const CrossFamilyWarning = ({ resolvedSelectedItems, familyName }: CrossFamilyWarningProps) => {
    const crossFamilyItems = useMemo(
        () => resolvedSelectedItems.filter((item) => item.familyName !== familyName),
        [resolvedSelectedItems, familyName]
    );

    if (!familyName) {
        return (
            <Alert variant='warning' style={{ marginBottom: 0, flexDirection: 'column', gap: tokens.space.xs }}>
                <strong>Cross-family paste</strong>
                <div>
                    The copied parts come from multiple families, so pasted values may not fit
                    every target item. Check the paste preview before submitting.
                </div>
            </Alert>
        );
    }

    if (crossFamilyItems.length === 0) return null;

    const targetFamilies = [...new Set(crossFamilyItems.map((i) => i.familyName))];

    const formatFamilyList = () => {
        const listed = targetFamilies.slice(0, MAX_LISTED);
        const items = listed.map((f, i) => (
            <React.Fragment key={f}>
                {i > 0 && ', '}
                <strong>{f}</strong>
            </React.Fragment>
        ));
        return targetFamilies.length > MAX_LISTED
            ? <>{items} and {targetFamilies.length - MAX_LISTED} more</>
            : <>{items}</>;
    };

    const formatItemList = () => {
        const listed = crossFamilyItems.slice(0, MAX_LISTED).map((i) => i.itemNo).join(', ');
        return crossFamilyItems.length > MAX_LISTED
            ? `${listed} and ${crossFamilyItems.length - MAX_LISTED} more`
            : listed;
    };

    return (
        <Alert variant='warning' style={{ marginBottom: 0, flexDirection: 'column', gap: tokens.space.xs }}>
            <strong>Cross-family paste</strong>
            <div>
                The copied parts are from <strong>{familyName}</strong>, but{' '}
                {crossFamilyItems.length === 1
                    ? <>1 target item ({formatItemList()}) belongs to</>
                    : <>{crossFamilyItems.length} target items ({formatItemList()}) belong to</>}{' '}
                {targetFamilies.length === 1 ? 'family' : 'other families'} {formatFamilyList()}.
            </div>
            <div>
                Check that they make sense for the target family,
                or deselect those items in the target table.
            </div>
        </Alert>
    );
};
